import {
  Box,
  Button,
  Grid,
  MenuItem,
  Radio,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useState,
  type ChangeEvent,
  type FC,
  useMemo,
  RefObject,
  useEffect,
} from "react";
import { ActionItem } from "../MagicPage";
import {
  API_ASYNC_INFER_SEGMENT_ANY,
  CLOTHE_SEGMENT_TYPE_ENUM,
  DEFAULT_LINE_COLOR,
  DEFAULT_LINE_WIDTH,
  ERR_MSG_LASSO_POINTS_NOT_ENOUGH,
  ERR_MSG_NOT_GET_ORIGIN_BASE64,
  ERR_MSG_NOT_LOGIN,
  ERR_MSG_NO_TASK_ID,
  INFER_TASK_STATUS_ENUM,
  ORIGIN_MASK_ACTIONS_ENUM,
  ORIGIN_MASK_SEGMENT_ACTIONS_ENUM,
  SEGMENT_TYPE_ENUM,
} from "../../utils/constants";
import { LassoPointType } from "../../utils/image.util";
import {
  SegmentAnyAPIRequestBodyType,
  cancelTask,
  getTaskResultEvery3s,
} from "../../utils/http.util";
import { MainMagicShortcutType } from "../main-page/Shortcuts";

const getSegmentTypeByDisplayStr = (display: string) => {
  switch (display) {
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.rmbg: {
      return SEGMENT_TYPE_ENUM.rmbg;
    }
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.clothe: {
      return SEGMENT_TYPE_ENUM.clothe;
    }
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.face: {
      return SEGMENT_TYPE_ENUM.face;
    }
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.down_long: {
      return CLOTHE_SEGMENT_TYPE_ENUM.down_long;
    }
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.down_short: {
      return CLOTHE_SEGMENT_TYPE_ENUM.down_short;
    }
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.low_cover_big: {
      return CLOTHE_SEGMENT_TYPE_ENUM.low_cover_big;
    }
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.upper_long: {
      return CLOTHE_SEGMENT_TYPE_ENUM.upper_long;
    }
    case ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.upper_short: {
      return CLOTHE_SEGMENT_TYPE_ENUM.upper_short;
    }
    // TODO
    default: {
      return SEGMENT_TYPE_ENUM.rmbg;
    }
  }
};

/*const getSegmentDisplayTypeByType = (
  type: SEGMENT_TYPE_ENUM | CLOTHE_SEGMENT_TYPE_ENUM | undefined
) => {
  switch (type) {
    case SEGMENT_TYPE_ENUM.rmbg: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.rmbg;
    }
    case SEGMENT_TYPE_ENUM.clothe: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.clothe;
    }
    case SEGMENT_TYPE_ENUM.face: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.face;
    }
    case CLOTHE_SEGMENT_TYPE_ENUM.down_long: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.down_long;
    }
    case CLOTHE_SEGMENT_TYPE_ENUM.down_short: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.down_short;
    }
    case CLOTHE_SEGMENT_TYPE_ENUM.low_cover_big: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.low_cover_big;
    }
    case CLOTHE_SEGMENT_TYPE_ENUM.upper_long: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.upper_long;
    }
    case CLOTHE_SEGMENT_TYPE_ENUM.upper_short: {
      return ORIGIN_MASK_SEGMENT_ACTIONS_ENUM.upper_short;
    }
    default: {
      return type;
    }
  }
};*/

// --------------------------------------------------------------------------------------------------------------
type OriginMaskActionsProps = {
  onResetClick: () => void;
  actionsDisabled: boolean;
  onMaskActionTypeChange: (type: ORIGIN_MASK_ACTIONS_ENUM) => void;
  onMaskPaintPenWidthChange: (penWidth: number) => void;
  onMaskEraserSizeChange: (eraserSize: number) => void;
  maskLassoPointsRef: RefObject<LassoPointType[]>;
  onLassoDoneClick: () => void;
  canvasRef_mask: RefObject<HTMLCanvasElement | null>;
  showPromptMsg: (msg: string) => void;
  onSegmentSuccess: (url: string, type: string) => void;
  onMagicGeneratingChange: (generating: boolean) => void;
  magicShortcut: MainMagicShortcutType | null;
  originFile: File | undefined;
  originCanvasRef: RefObject<HTMLCanvasElement | null>;
  magicUrl: string;
  onRadioChange: () => void;
  radioValue: "origin" | "mask";
  disableFirstBtn: boolean;
};

export const OriginMaskActions: FC<OriginMaskActionsProps> = (props) => {
  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  const [actionType, setActionType] = useState(ORIGIN_MASK_ACTIONS_ENUM.paint); // ORIGIN_MASK_ACTIONS_ENUM
  const [penWidth, setPenWidth] = useState(DEFAULT_LINE_WIDTH);
  const [eraserWidth, setEraserWidth] = useState(DEFAULT_LINE_WIDTH);
  const [isLassoDoneClicked, setIsLassoDoneClicked] = useState(false);
  const [segmentTypeDisplay, setSegmentTypeDisplay] = useState(""); // ORIGIN_MASK_SEGMENT_ACTIONS_ENUM
  const [taskId, setTaskId] = useState("");
  const [taskStatus, setTaskStatus] = useState<string>(
    INFER_TASK_STATUS_ENUM.down
  );
  const [totalTasks, setTotalTasks] = useState(0);

  const onActionTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as unknown as ORIGIN_MASK_ACTIONS_ENUM;
    if (value !== actionType) {
      setActionType(value);
      props.onMaskActionTypeChange(value);

      if (value === ORIGIN_MASK_ACTIONS_ENUM.segment) {
        setSegmentTypeDisplay("");
        setTaskId("");
        setTaskStatus(INFER_TASK_STATUS_ENUM.down);
        setTotalTasks(0);
      }
    }

    if (value === ORIGIN_MASK_ACTIONS_ENUM.paint) {
      setPenWidth(DEFAULT_LINE_WIDTH);
      props.onMaskPaintPenWidthChange(DEFAULT_LINE_WIDTH);
    }

    if (value === ORIGIN_MASK_ACTIONS_ENUM.eraser) {
      setEraserWidth(DEFAULT_LINE_WIDTH);
      props.onMaskEraserSizeChange(DEFAULT_LINE_WIDTH);
    }

    if (value !== ORIGIN_MASK_ACTIONS_ENUM.lasso) {
      setIsLassoDoneClicked(false);
    }
  };

  const onPenChange = (_e: Event, newValue: number) => {
    setPenWidth(newValue);
    props.onMaskPaintPenWidthChange(newValue);
  };

  const onEraserChange = (_e: Event, newValue: number) => {
    setEraserWidth(newValue);
    props.onMaskEraserSizeChange(newValue);
  };

  const actions: ActionItem[] = useMemo(
    () => [
      {
        label: ORIGIN_MASK_ACTIONS_ENUM.paint,
        value: penWidth,
        onChange: onPenChange,
        min: 1,
        max: 20,
        step: 1,
      },
      {
        label: ORIGIN_MASK_ACTIONS_ENUM.eraser,
        value: eraserWidth,
        onChange: onEraserChange,
        min: 1,
        max: 20,
        step: 1,
      },
      {
        label: ORIGIN_MASK_ACTIONS_ENUM.lasso,
      },
      {
        label: ORIGIN_MASK_ACTIONS_ENUM.segment,
      },
    ],
    // eslint-disable-next-line
    [penWidth, eraserWidth]
  );

  // 确定套索
  const handleMaskLassoDoneClick = () => {
    console.log(
      " -> OriginMaskActions-handleMaskLassoDoneClick, maskLassoPointsRef.current:",
      props.maskLassoPointsRef.current
    );

    setIsLassoDoneClicked(true);

    const points = props.maskLassoPointsRef.current;

    if (points.length < 3) {
      props.showPromptMsg(ERR_MSG_LASSO_POINTS_NOT_ENOUGH);
    } else {
      var canvas = props.canvasRef_mask.current;
      if (!canvas) return;

      var ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.fillStyle = DEFAULT_LINE_COLOR;
      ctx.strokeStyle = DEFAULT_LINE_COLOR;
      ctx.lineWidth = DEFAULT_LINE_WIDTH * 2;
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (var i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.fill();

      props.onLassoDoneClick();
    }
  };

  // 分割 任务
  const handleMaskTaskIdUpdate = (tid: string) => setTaskId(tid);

  const handleMaskTaskStatusUpdate = (status: string, idx: number) => {
    console.log(
      " -> OriginMaskActions-handleMaskTaskStatusUpdate, status:",
      status,
      ", idx:",
      idx
    );

    setTaskStatus(status);
    setTotalTasks(idx);
  };

  const handleMaskGetTaskResult = (
    data: any,
    type: SEGMENT_TYPE_ENUM | CLOTHE_SEGMENT_TYPE_ENUM
  ) => {
    console.log(
      "-> OriginMaskActions-handleMaskGetTaskResult, segmentType:",
      type,
      ", response:",
      data
    );

    if (data === ERR_MSG_NOT_LOGIN) {
      props.showPromptMsg(ERR_MSG_NOT_LOGIN);
    } else if (typeof data === "string" && data.includes(":")) {
      // "5: task a241ef337ab7987d has been canceled"
      const code = data.split(": ")[0];
      if (parseInt(code) === 5) {
        setTaskStatus(INFER_TASK_STATUS_ENUM.down);
        setTaskId("");
        setTotalTasks(0);
      } else {
        props.showPromptMsg(data);
      }
    } else {
      switch (type) {
        case SEGMENT_TYPE_ENUM.rmbg: {
          if (data?.result && data.result?.rmbg_mask) {
            // "segment_prompt": "rmbg", - 这种，返回 rmbg_mask, rmbg_rgba 两张图的 url
            props.onSegmentSuccess(data.result.rmbg_mask, type ?? "");
          }
          break;
        }
        case SEGMENT_TYPE_ENUM.face: {
          if (data?.result && data.result?.face_mask) {
            // 返回 face_mask, face_rgba, rmbg_mask, rmbg_rgba 四张图的 url
            props.onSegmentSuccess(data.result.face_mask, type ?? "");
          }
          break;
        }
        default: {
          if (data?.result && data.result?.clothe_mask) {
            // 返回 clothe_mask, clothe_rgba, rmbg_mask, rmbg_rgba 四张图的 url
            props.onSegmentSuccess(data.result.clothe_mask, type ?? "");
          }
          break;
        }
      }
    }
  };

  const handleCancelSegmentTaskClick = async () => {
    console.log(
      "-> OriginMaskActions-handleCancelSegmentTaskClick, totalTasks:",
      totalTasks,
      ", taskId:",
      taskId
    );

    if (taskId) {
      const data = await cancelTask(taskId);
      if (data === ERR_MSG_NOT_LOGIN) {
        props.showPromptMsg(ERR_MSG_NOT_LOGIN);
      } else if (typeof data === "string" && data.includes(":")) {
        props.showPromptMsg(data);
      } else {
        setTaskStatus(INFER_TASK_STATUS_ENUM.down);
        setTaskId("");
        setTotalTasks(0);
      }
    } else {
      props.showPromptMsg(ERR_MSG_NO_TASK_ID);
    }
  };

  const originMaskGetOriginBase64 = () => {
    if (props.originFile) {
      const base64 = props.originCanvasRef?.current?.toDataURL("image/jpeg");

      if (base64) {
        return base64;
      }
    }

    console.log(
      " -> OriginMaskActions-originMaskGetOriginBase64, originFile:",
      props.originFile
    );

    return "";
  };

  useEffect(() => {
    if (
      taskStatus === INFER_TASK_STATUS_ENUM.inProgress ||
      (taskStatus === INFER_TASK_STATUS_ENUM.waiting && totalTasks < 1)
    ) {
      props.onMagicGeneratingChange(true);
    } else {
      props.onMagicGeneratingChange(false);
    }
    // eslint-disable-next-line
  }, [taskStatus]);

  useEffect(() => {
    if (!props.magicUrl) {
      setActionType(ORIGIN_MASK_ACTIONS_ENUM.paint);
      setIsLassoDoneClicked(false);
      setSegmentTypeDisplay("");
      setTaskId("");
      setTaskStatus(INFER_TASK_STATUS_ENUM.down);
      setTotalTasks(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.magicUrl]);

  useEffect(() => {
    if (props.radioValue === "origin") {
      props.onResetClick();
      setIsLassoDoneClicked(false);

      setActionType(ORIGIN_MASK_ACTIONS_ENUM.paint);
      props.onMaskActionTypeChange(ORIGIN_MASK_ACTIONS_ENUM.paint);

      setPenWidth(DEFAULT_LINE_WIDTH);
      props.onMaskPaintPenWidthChange(DEFAULT_LINE_WIDTH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.radioValue]);

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "0 16px",
        flexWrap: notPhone ? "nowrap" : "wrap",
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: "120px",
        }}
      >
        <Radio
          checked={props.radioValue === "mask"}
          onChange={() => {
            if (props.magicShortcut?.params.segment_prompt) {
              setActionType(ORIGIN_MASK_ACTIONS_ENUM.segment);
              props.onMaskActionTypeChange(ORIGIN_MASK_ACTIONS_ENUM.segment);

              setSegmentTypeDisplay("");
              setTaskId("");
              setTaskStatus(INFER_TASK_STATUS_ENUM.down);
              setTotalTasks(0);
            } else {
              setActionType(ORIGIN_MASK_ACTIONS_ENUM.paint);
              props.onMaskActionTypeChange(ORIGIN_MASK_ACTIONS_ENUM.paint);

              setPenWidth(DEFAULT_LINE_WIDTH);
              props.onMaskPaintPenWidthChange(DEFAULT_LINE_WIDTH);
            }
            setIsLassoDoneClicked(false);

            props.onRadioChange();
          }}
          value="mask"
          disabled={props.disableFirstBtn}
          color={
            props.magicShortcut?.params.segment_prompt ? "secondary" : "primary"
          }
        />
        <Typography
          variant="body2"
          color={
            props.disableFirstBtn
              ? "textDisabled"
              : props.radioValue === "mask"
              ? props.magicShortcut?.params.segment_prompt
                ? "secondary"
                : "primary"
              : "textPrimary"
          }
        >
          重绘区操作
        </Typography>
      </Box>

      <TextField
        select
        label="重绘区操作"
        placeholder="重绘操作"
        size="small"
        value={actionType}
        onChange={onActionTypeChange}
        sx={{
          minWidth: "120px",
        }}
        disabled={props.actionsDisabled}
        color={
          props.magicShortcut?.params.segment_prompt ? "secondary" : "primary"
        }
      >
        {actions.map((act, idx) => (
          <MenuItem key={`origin-mask-actions-type-${idx}`} value={act.label}>
            {act.label}
          </MenuItem>
        ))}
      </TextField>

      {actions
        .filter((act) => act.label === actionType)
        .map(
          (act, idx) =>
            act.onChange && (
              <Slider
                key={`origin-mask-actions-slider-${idx}`}
                value={act.value}
                onChange={act.onChange}
                min={act.min ?? 0}
                max={act.max ?? 100}
                step={act.step ?? 1}
                size="small"
                disabled={props.actionsDisabled}
                color={
                  props.magicShortcut?.params.segment_prompt
                    ? "secondary"
                    : "primary"
                }
              />
            )
        )}

      {actionType === ORIGIN_MASK_ACTIONS_ENUM.lasso && (
        <Button
          variant="contained"
          size="small"
          onClick={handleMaskLassoDoneClick}
          disabled={
            isLassoDoneClicked ||
            props.maskLassoPointsRef.current.length < 3 ||
            props.actionsDisabled
          }
          color={
            props.magicShortcut?.params.segment_prompt ? "secondary" : "primary"
          }
        >
          确定套索
        </Button>
      )}

      {actionType === ORIGIN_MASK_ACTIONS_ENUM.segment && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: "8px",
          }}
        >
          <TextField
            select
            label="分割操作"
            placeholder="分割操作"
            size="small"
            value={segmentTypeDisplay}
            onChange={async (e: ChangeEvent<HTMLInputElement>) => {
              const curSegmentTypeDisplay = e.target.value;
              setSegmentTypeDisplay(curSegmentTypeDisplay);

              const originBase64 = originMaskGetOriginBase64();

              if (originBase64) {
                const curSegmentType = getSegmentTypeByDisplayStr(
                  curSegmentTypeDisplay
                );

                const requestBody: SegmentAnyAPIRequestBodyType = {
                  init_image: originBase64.split(",")[1],
                  segment_prompt: curSegmentType,
                  prompt: "",
                };

                const taskResultResp = await getTaskResultEvery3s(
                  API_ASYNC_INFER_SEGMENT_ANY,
                  requestBody,
                  handleMaskTaskIdUpdate,
                  handleMaskTaskStatusUpdate
                );

                handleMaskGetTaskResult(taskResultResp, curSegmentType);
              } else {
                props.showPromptMsg(
                  `分割-${curSegmentTypeDisplay}：${ERR_MSG_NOT_GET_ORIGIN_BASE64}`
                );
              }
            }}
            sx={{ minWidth: "110px" }}
            disabled={props.actionsDisabled}
            color={
              props.magicShortcut?.params.segment_prompt
                ? "secondary"
                : "primary"
            }
          >
            {Object.values(ORIGIN_MASK_SEGMENT_ACTIONS_ENUM).map(
              (segment, idx) => (
                <MenuItem key={`origin-actions-type-${idx}`} value={segment}>
                  {segment}
                </MenuItem>
              )
            )}
          </TextField>

          {actionType === ORIGIN_MASK_ACTIONS_ENUM.segment &&
            taskStatus === INFER_TASK_STATUS_ENUM.waiting && (
              <Button
                size="small"
                variant="contained"
                onClick={handleCancelSegmentTaskClick}
                color="warning"
              >
                取消任务
              </Button>
            )}

          {actionType === ORIGIN_MASK_ACTIONS_ENUM.segment && (
            <Typography
              variant="body2"
              color={
                taskStatus === INFER_TASK_STATUS_ENUM.waiting
                  ? "warning"
                  : props.magicShortcut?.params.segment_prompt
                  ? "secondary"
                  : "primary"
              }
              noWrap
            >
              {taskStatus === INFER_TASK_STATUS_ENUM.inProgress
                ? "分割中..."
                : taskStatus === INFER_TASK_STATUS_ENUM.waiting
                ? `排队中，${totalTasks}个任务`
                : "请选择分割类型"}
            </Typography>
          )}
        </Box>
      )}

      <Button
        variant="outlined"
        size="small"
        onClick={() => {
          props.onResetClick();
          setIsLassoDoneClicked(false);
        }}
        disabled={props.actionsDisabled}
        color={
          props.magicShortcut?.params.segment_prompt ? "secondary" : "primary"
        }
      >
        重绘
      </Button>
    </Grid>
  );
};
